// ── 킵목록 모달 컴포넌트 ─────────────────────────────────────────────────────
// - 킵 등록된 미팅 전체 목록
// - 관리자: 킵 해제 버튼 / 마케터: 숨기기 버튼 (localStorage 유지)
// - 내부에서 hiddenKeepIds / keepShowAll 상태 관리

import React, { useState } from 'react';
import Modal from '../../../components/Modal';
import { apiClient } from '../../../api/apiClient';
import { HQ_MEETING_API } from '../meetingMapApi';
import {
  ghostActionStyle, emptyStyle,
  keepItemStyle, keepItemTopStyle, keepItemBottomStyle, keepRemoveBtnStyle,
  keepHideBtnStyle, keepViewToggleBtnStyle,
  pendingItemDateStyle, pendingItemTimeStyle, pendingItemTmStyle,
  pendingItemCompanyStyle,
} from '../../../components/meetingmap/styles';

export function KeepListModal({
  open,
  onClose,
  keepListLoading,
  keepList,
  setKeepList,    // 관리자 킵 해제 후 즉시 목록 갱신
  setMeetings,    // 킵 해제 후 미팅 목록 반영
  adminMode,
  api = HQ_MEETING_API,   // 관리자 주소 묶음(2026-10-22)
  showToast,
}) {
  // ── 마케터 숨김 목록: localStorage 영구 보관 ────────────────────────────
  const [hiddenKeepIds, setHiddenKeepIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('hidden-keep-ids') || '[]')); }
    catch { return new Set(); }
  });
  // false = 숨긴것 제외 보기, true = 전체 보기
  const [keepShowAll, setKeepShowAll] = useState(false);

  function toggleHideItem(id) {
    setHiddenKeepIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      try { localStorage.setItem('hidden-keep-ids', JSON.stringify([...next])); } catch {}
      return next;
    });
  }

  function handleClose() {
    setKeepShowAll(false);
    onClose();
  }

  // 관리자 킵 해제 핸들러
  async function handleAdminRemove(item) {
    const res = await apiClient.patch(`${api.base}/${item.id}/keep`, { is_keep: false });
    if (res.ok) {
      setKeepList((prev) => prev.filter((k) => k.id !== item.id));
      setMeetings((prev) =>
        prev.map((m) => Number(m.id) === Number(item.id) ? { ...m, is_keep: false } : m)
      );
      showToast('킵목록에서 제거되었습니다.', 'success');
    } else {
      showToast('킵 해제에 실패했습니다.', 'error');
    }
  }

  // 마케터 뷰: 숨김 필터 적용
  const visibleList = (!adminMode && !keepShowAll)
    ? keepList.filter((item) => !hiddenKeepIds.has(item.id))
    : keepList;
  const hiddenCount = adminMode ? 0 : keepList.filter((item) => hiddenKeepIds.has(item.id)).length;

  return (
    <Modal
      open={open}
      title="☆ 킵목록"
      icon="⭐"
      onClose={handleClose}
      width="min(480px, 96vw)"
      mobileSheet
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" style={ghostActionStyle} onClick={handleClose}>
            닫기
          </button>
        </div>
      )}
    >
      {keepListLoading && <div style={emptyStyle}>불러오는 중...</div>}
      {!keepListLoading && keepList.length === 0 && (
        <div style={emptyStyle}>킵목록에 추가된 매장이 없습니다.</div>
      )}

      {!keepListLoading && keepList.length > 0 && (
        <div>
          {/* 마케터 뷰: 전체보기 / 숨긴것제외 토글 */}
          {!adminMode && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginBottom: 10 }}>
              {hiddenCount > 0 && (
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>숨긴 항목 {hiddenCount}개</span>
              )}
              <button
                type="button"
                style={keepViewToggleBtnStyle(keepShowAll)}
                onClick={() => setKeepShowAll((v) => !v)}
              >
                {keepShowAll ? '숨긴것 제외 보기' : '전체 보기'}
              </button>
            </div>
          )}

          <div style={{ display: 'grid', gap: 8 }}>
            {visibleList.map((item) => {
              const isHidden = hiddenKeepIds.has(item.id);
              return (
                <div key={item.id} style={{ ...keepItemStyle, opacity: isHidden ? 0.45 : 1 }}>
                  <div style={keepItemTopStyle}>
                    <span style={pendingItemDateStyle}>{item.visit_date || '-'}</span>
                    {item.visit_time && <span style={pendingItemTimeStyle}>{item.visit_time}</span>}
                    {/* 관리자: TM 담당자 / 마케터 표시 */}
                    {adminMode && (item.tm_name || item.sales_person_name) && (
                      <span style={pendingItemTmStyle}>
                        {[item.tm_name, item.sales_person_name].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </div>
                  <div style={keepItemBottomStyle}>
                    <div style={{ minWidth: 0 }}>
                      <strong style={pendingItemCompanyStyle}>{item.company_name || '-'}</strong>
                      {item.industry && (
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 6 }}>{item.industry}</span>
                      )}
                      {item.ticket_type && (
                        <div style={{ fontSize: 11, color: 'var(--mm-tone-blue-text)', fontWeight: 600, marginTop: 2 }}>
                          {item.ticket_type}
                        </div>
                      )}
                    </div>
                    {/* 관리자: 킵 해제 버튼 / 마케터: 숨기기 버튼 */}
                    {adminMode ? (
                      <button
                        type="button"
                        style={keepRemoveBtnStyle}
                        onClick={() => handleAdminRemove(item)}
                      >
                        해제
                      </button>
                    ) : (
                      <button
                        type="button"
                        style={keepHideBtnStyle(isHidden)}
                        onClick={() => toggleHideItem(item.id)}
                        title={isHidden ? '다시 표시' : '목록에서 숨기기'}
                      >
                        {isHidden ? '표시' : '숨기기'}
                      </button>
                    )}
                  </div>
                  {item.address && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{item.address}</div>
                  )}
                </div>
              );
            })}
            {visibleList.length === 0 && (
              <div style={emptyStyle}>표시할 항목이 없습니다. 전체 보기를 눌러보세요.</div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
